import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { uploadMedia } from 'store/media/mediaAsync';
import { mediaActions } from 'store/media/mediaSlice';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Button } from 'components/Buttons';
import { Checkbox, Input } from 'components/Controls';

type Props = {
  open: boolean;
  onClose: () => void;
}

const UploadAudioDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const quiz = useAppSelector(selectQuiz);

  const [isLoading, setIsLoading] = useState(false);
  const [uploadToAccount, setUploadToAccount] = useState<boolean>(false);
  const [fileDataUrl, setFileDataUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState<string>('');

  const handleChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }

  const handleChangeUploadToAccount = (e: ChangeEvent<HTMLInputElement>) => {
    setUploadToAccount(e.target.checked);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || !files[0]) return;

    const selectedFile = files[0];
    setFile(selectedFile);

    const fileReader = new FileReader();
    fileReader.onload = (event: ProgressEvent<FileReader>) => {
      if (!event.target) return;
      setFileDataUrl(event.target.result as string);
    };
    fileReader.readAsArrayBuffer(selectedFile);
  };

  const handleClickApply = async () => {
    if (!fileDataUrl) return;

    setIsLoading(true);

    try {
      const blob = new Blob([fileDataUrl], { type: file?.type });
      const data: any = { blob, type: 'audio', title };
      if (!uploadToAccount && quiz) {
        data['quizId'] = quiz._id;
      }
      const media = await dispatch(uploadMedia(data)).unwrap();
      dispatch(mediaActions.setSelectedMedia(media));
      onClose();
    } catch (error) { } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const currentInputRef = inputRef?.current;

    if (currentInputRef) {
      currentInputRef.click();
      currentInputRef.addEventListener('cancel', onClose);
    }

    return () => {
      if (currentInputRef) currentInputRef.removeEventListener('cancel', onClose);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Dialog size="lg" open={open} onClose={onClose}>
      <DialogTitle title={t('dialogs.uploadAudio.title')} onClose={onClose} />
      <DialogContent>
        {isLoading ? (
          <h1 className="text-white font-semibold">{`${t('labels.loading')} ...`}</h1>
        ) : (
          <span className="text-white text-xs font-light">{file?.name || ''}</span>
        )}
        {file ? (
          <div className="pt-2">
            <Input id="media-audio-title" name="media-audio-title" label="Title" onChange={handleChangeTitle} value={title} />
          </div>
        ) : null}
        <input
          ref={inputRef}
          className="hidden"
          type="file"
          accept="audio/mpeg,audio/mp3,audio/wav"
          onChange={handleChange}
        />
        {quiz ? (
          <Checkbox
            className="my-2"
            label={t('labels.uploadToAccountMedia')}
            id="upload-to-account-media"
            name="upload-to-account-media"
            checked={uploadToAccount}
            onChange={handleChangeUploadToAccount}
            disabled={isLoading}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('buttons.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!file}
          loading={isLoading}
          onClick={handleClickApply}
        >
          {t('buttons.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadAudioDialog;
